import { useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DocumentMessageBubbleProps {
  messageId: string;
  metadata: any;
  isUserMessage: boolean;
}

export function DocumentMessageBubble({
  messageId,
  metadata,
  isUserMessage,
}: DocumentMessageBubbleProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const media = metadata?.media ?? {};
  const fileName: string =
    media?.fileName || metadata?.fileName || "Documento";
  const hasOpenable = Boolean(
    media?.url || metadata?.object_key || media?.base64,
  );

  const handleOpen = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Legacy fallback: inline base64 (never re-fetched from DB going forward)
      if (!media?.url && !metadata?.object_key && media?.base64) {
        const mime = media?.mimetype || "application/octet-stream";
        window.open(`data:${mime};base64,${media.base64}`, "_blank", "noopener");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "presign-message-media",
        { body: { message_id: messageId } },
      );
      if (error || !data?.url) {
        throw new Error("no_url");
      }
      window.open(data.url as string, "_blank", "noopener");
    } catch {
      toast({
        title: "Não foi possível abrir o documento",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-muted mb-2 max-w-[300px]">
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
          isUserMessage ? "chat-bubble-user/20" : "bg-primary/10"
        }`}
      >
        <FileText
          className={`w-5 h-5 ${isUserMessage ? "chat-bubble-user-fg" : "text-primary"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{fileName}</p>
        <p className="text-xs text-muted-foreground">Documento</p>
      </div>
      {hasOpenable && (
        <button
          type="button"
          onClick={handleOpen}
          disabled={loading}
          className={`${
            isUserMessage
              ? "chat-bubble-user-fg hover:underline"
              : "text-primary hover:underline"
          } text-xs flex-shrink-0 inline-flex items-center gap-1 disabled:opacity-60`}
        >
          {loading ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Abrindo…
            </>
          ) : (
            <>Abrir ↗</>
          )}
        </button>
      )}
    </div>
  );
}
